/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, memo, Suspense, useEffect, useMemo, useRef } from 'react';

import { keyframes, cssMap as unboundCssMap } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { ProviderFactory, type Providers } from '@atlaskit/editor-common/provider-factory';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import ProfileCardClient from '@atlaskit/profilecard/profile-card-client';
import { type RendererProps } from '@atlaskit/renderer';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { extractMarkdownText } from '@atlassian/conversation-assistant-content-transformer/utils/stream-markdown-parser';
import { SmartMentionResource } from '@atlassian/smart-mention-resource';
import { UserProfileCardForMention } from '@atlassian/user-profile-card/mention';

import { SAIN_CITATION_SELECTOR } from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/citation-selectors';
import {
	InlineCitation,
	type InlineCitationProps,
} from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/inline-citation/inline-citation';
import {
	StatusCitation,
	type StatusCitationProps,
} from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/status-citation';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerDialogClick } from '../../../ai-answer-dialog/on-ai-answer-dialog-click';
import {
	LUMINA_END_DELIMITER,
	LUMINA_PLACEHOLDER_TAG,
	LUMINA_START_DELIMITER,
} from '../../../ai-answer-dialog/utils/fetchAIAnswer';
import { getBlueLinkCount } from '../../../ai-answer-dialog/utils/get-blue-link-count';
import { SainAction } from '../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { renderStatusComponent, type StatusComponentProps } from './render-status-component';

const messages = defineMessages({
	linksOpenInNewTab: {
		id: 'search-ai.v2.common.react-renderer.linksOpenInNewTab',
		defaultMessage: 'Links in this answer open in a new tab.',
		description:
			'Hidden screen reader prompt informing users that links in an AI answer open in a new tab.',
	},
});

const AkReactRenderer = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_.renderer" */ '@atlaskit/renderer').then(
		({ ReactRenderer }) => {
			return {
				default: ReactRenderer,
			};
		},
	),
);

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const animatedStyles = cssMap({
	container: {
		animation: `${fadeIn} 700ms cubic-bezier(0.3,0,0.8,0.15) forwards`,
	},
});

const unboundAnimatedStyles = unboundCssMap({
	container: {
		opacity: 0,
	},
	listItem: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > li::marker': {
			opacity: 0,
			animation: `${fadeIn} 700ms cubic-bezier(0.3,0,0.8,0.15) forwards`,
		},
	},
});

const StaggeredChild = memo(({ children, index }: { children: React.ReactNode; index: number }) => {
	// If children is a string longer than 10 characters, split it into 3 parts
	if (typeof children === 'string' && children.length > 10) {
		const chunkSize = Math.ceil(children.length / 3);
		const parts = [
			children.slice(0, chunkSize),
			children.slice(chunkSize, chunkSize * 2),
			children.slice(chunkSize * 2),
		];

		return (
			<Fragment>
				{parts.map((part, partIndex) => (
					<span
						key={partIndex}
						css={[animatedStyles.container, unboundAnimatedStyles.container]}
						style={{
							animationDelay: `${index * 200 + partIndex * 100}ms`,
						}}
					>
						{part}
					</span>
				))}
			</Fragment>
		);
	}

	// For non-strings or short strings, render normally
	return (
		<span
			css={[animatedStyles.container, unboundAnimatedStyles.container]}
			style={{
				animationDelay: `${index * 200}ms`,
			}}
		>
			{children}
		</span>
	);
});

StaggeredChild.displayName = 'StaggeredChild';

const StaggeredAnimatedParagraph = memo(({ children }: { children: React.ReactNode }) => {
	// Convert children to array to handle staggering
	const childrenArray = React.Children.toArray(children);

	return (
		<Box>
			{childrenArray.map((child, index) => (
				<StaggeredChild key={index} index={index}>
					{child}
				</StaggeredChild>
			))}
		</Box>
	);
});

StaggeredAnimatedParagraph.displayName = 'StaggeredAnimatedParagraph';

const AnimatedOrderedList = memo(
	({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => {
		return (
			<ol
				{...props}
				css={[
					animatedStyles.container,
					unboundAnimatedStyles.container,
					unboundAnimatedStyles.listItem,
				]}
			>
				{children}
			</ol>
		);
	},
);

AnimatedOrderedList.displayName = 'AnimatedOrderedList';

const AnimatedBulletedList = memo(
	({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => {
		return (
			<ul
				{...props}
				css={[
					animatedStyles.container,
					unboundAnimatedStyles.container,
					unboundAnimatedStyles.listItem,
				]}
			>
				{children}
			</ul>
		);
	},
);

AnimatedBulletedList.displayName = 'AnimatedBulletedList';

const AnimatedHeading = memo(
	({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => {
		// Extract heading level from props (h1, h2, h3, etc.)
		const headingLevel = props.level || 1;
		const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;

		return (
			<HeadingTag {...props} css={[animatedStyles.container, unboundAnimatedStyles.container]}>
				{children}
			</HeadingTag>
		);
	},
);

AnimatedHeading.displayName = 'AnimatedHeading';

// Create stable component references to prevent recreation
const StatusComponent = memo((props: StatusCitationProps) => <StatusCitation {...props} />);
StatusComponent.displayName = 'StatusComponent';

const StatusComponentInlineCitation = memo((props: InlineCitationProps) => (
	<InlineCitation {...props} />
));
StatusComponentInlineCitation.displayName = 'StatusComponentInlineCitation';

const MediaComponent = memo(() => <Fragment />);
MediaComponent.displayName = 'MediaComponent';

const MediaSingleComponent = memo(() => <Fragment />);
MediaSingleComponent.displayName = 'MediaSingleComponent';

// Create stable component references
const StreamingParagraphComponent = memo((props: any) => <StaggeredAnimatedParagraph {...props} />);
StreamingParagraphComponent.displayName = 'StreamingParagraphComponent';

const StreamingOrderedListComponent = memo((props: any) => <AnimatedOrderedList {...props} />);
StreamingOrderedListComponent.displayName = 'StreamingOrderedListComponent';

const StreamingBulletedListComponent = memo((props: any) => <AnimatedBulletedList {...props} />);
StreamingBulletedListComponent.displayName = 'StreamingBulletedListComponent';

const StreamingHeadingComponent = memo((props: any) => <AnimatedHeading {...props} />);
StreamingHeadingComponent.displayName = 'StreamingHeadingComponent';

export function ReactRenderer({
	markdown,
	animateComponents = false,
}: {
	markdown: string;
	animateComponents?: boolean;
}): JSX.Element {
	const { luminaContentMap, commonAttributes, userDetails } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const rendererContainerRef = useRef<HTMLDivElement | null>(null);
	const blueLinkAnalyticsContextRef = useRef({
		commonAttributes,
		fireAnalyticsEvent,
		fireSAINUsageEvent,
	});
	blueLinkAnalyticsContextRef.current = {
		commonAttributes,
		fireAnalyticsEvent,
		fireSAINUsageEvent,
	};

	const useInlineCitations = SAINFeatureControl.isInlineCitationsUpdateEnabled();

	const luminaInMarkdownDataRef = useRef(luminaContentMap);
	luminaInMarkdownDataRef.current = luminaContentMap;

	// Keep the latest app context / user id in refs so that the memoized `status`
	// node component below can read fresh values without changing its identity on
	// every streamed chunk. `commonAttributes` gets a new reference on every chunk
	// (it depends on the growing answer string), so depending on it directly would
	// recreate the `status` component type each chunk and force the ADF renderer to
	// unmount/remount every inline status node (including the inline Lumina link
	// list), causing a resolved <-> loading flicker while streaming.
	const statusAppContextRef = useRef({
		orgId: commonAttributes.orgId,
		cloudId: commonAttributes.cloudId,
		sourceProduct: commonAttributes.sourceProduct ?? '',
		userId: userDetails?.id ?? '',
	});
	statusAppContextRef.current = {
		orgId: commonAttributes.orgId,
		cloudId: commonAttributes.cloudId,
		sourceProduct: commonAttributes.sourceProduct ?? '',
		userId: userDetails?.id ?? '',
	};

	const enableImageSearch = fg('hybrid_orchestrator_enable_image_search');
	const isBlueLinkAnalyticsEnabled = fg('kd_sain_blue_links_analytics');
	const hasBlueLinks = useMemo(
		() => isBlueLinkAnalyticsEnabled && getBlueLinkCount(markdown) > 0,
		[isBlueLinkAnalyticsEnabled, markdown],
	);

	const docContent = useMemo(() => {
		const processedMarkdown = fg('kd_sain_enable_inline_lumina')
			? extractMarkdownText(
					markdown,
					LUMINA_START_DELIMITER,
					LUMINA_END_DELIMITER,
					LUMINA_PLACEHOLDER_TAG,
				).processedText
			: markdown;

		return encodeAdf(
			processedMarkdown,
			true,
			undefined,
			undefined,
			useInlineCitations || undefined,
		);
	}, [markdown, useInlineCitations]);

	// The inline Lumina `status` node component. We build BOTH variants below (each
	// with a constant-length dependency array, so React's hook book-keeping is never
	// disturbed) and select between them via the feature gate. This keeps the
	// behaviour change gated per experimentation policy without introducing a
	// variable-length `useMemo` dependency array.
	const isStreamingFlickerFixEnabled = fg('kd_link_list_streaming_flicker_fix');

	// Fix ON: stable component identity for the renderer's lifetime. It reads the
	// latest app context / user id from `statusAppContextRef` (synchronised every
	// render above), so it never needs to be recreated as the answer streams in. A
	// stable `status` component type lets the ADF renderer reconcile status nodes in
	// place instead of unmounting/remounting them (and the inline Lumina link list)
	// on every chunk, which is what causes the resolved <-> loading flicker.
	const stableStatusComponent = useMemo(() => {
		return (props: StatusComponentProps) => {
			const { orgId, cloudId, sourceProduct, userId } = statusAppContextRef.current;
			return renderStatusComponent(
				props,
				luminaInMarkdownDataRef.current,
				{
					orgId,
					cloudId,
					sourceProduct,
				},
				userId,
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fix OFF: original behaviour — the component is recreated whenever
	// `commonAttributes` / user id change (which happens on every streamed chunk).
	const legacyStatusComponent = useMemo(() => {
		return (props: StatusComponentProps) => {
			return renderStatusComponent(
				props,
				luminaInMarkdownDataRef.current,
				{
					orgId: commonAttributes.orgId,
					cloudId: commonAttributes.cloudId,
					sourceProduct: commonAttributes.sourceProduct ?? '',
				},
				userDetails?.id ?? '',
			);
		};
	}, [commonAttributes, userDetails?.id]);

	const StatusComponentMemoized = isStreamingFlickerFixEnabled
		? stableStatusComponent
		: legacyStatusComponent;

	const config: RendererProps = useMemo(() => {
		const statusComponent = fg('kd_sain_enable_inline_lumina')
			? StatusComponentMemoized
			: useInlineCitations
				? StatusComponentInlineCitation
				: StatusComponent;

		// Create stable nodeComponents objects
		const staticNodeComponents = {
			status: statusComponent,
			media: MediaComponent,
			mediaSingle: MediaSingleComponent,
		};

		const animatedNodeComponents = {
			status: statusComponent,
			media: MediaComponent,
			mediaSingle: MediaSingleComponent,
			paragraph: StreamingParagraphComponent,
			orderedList: StreamingOrderedListComponent,
			bulletList: StreamingBulletedListComponent,
			heading: StreamingHeadingComponent,
		};

		const nodeComponents = animateComponents
			? { ...animatedNodeComponents }
			: { ...staticNodeComponents };
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { media, mediaSingle, ...nodeComponentsWithoutMediaOverrides } = nodeComponents;

		return {
			document: docContent,
			nodeComponents: enableImageSearch ? nodeComponentsWithoutMediaOverrides : nodeComponents,
			smartLinks: {
				ssr: true,
			},
		};
	}, [
		docContent,
		animateComponents,
		enableImageSearch,
		useInlineCitations,
		StatusComponentMemoized,
	]);

	const cloudId = commonAttributes.cloudId;
	const orgId = commonAttributes.orgId;
	const productKey = commonAttributes.sourceProduct;
	const viewerId = userDetails?.id;

	const dataProviders = useMemo(() => {
		if (!fg('kd_sain_clickable_user_mentions') || !cloudId || !productKey) {
			return undefined;
		}

		const mentionProvider: Providers['mentionProvider'] = Promise.resolve(
			new SmartMentionResource({
				productKey,
				siteId: cloudId,
				orgId,
				includeTeams: true,
				includeUsers: true,
				shouldHighlightMention(mention) {
					return mention.id === viewerId;
				},
			}),
		);

		const profilecardProvider: Providers['profilecardProvider'] = Promise.resolve({
			cloudId,
			resourceClient: new ProfileCardClient({
				url: '/gateway/api/directory/graphql',
				cloudId,
				productIdentifier: productKey,
			}),
			getActions: () => [],
			...(fg('people-teams_migrate-user-profile-card') ||
			FeatureGates.getExperimentValue('pt_user_profile_card_migration_exp', 'isEnabled', false)
				? {
						renderUserMentionCard: ({
							userId,
							cloudId: mentionCloudId,
							children,
						}: {
							userId: string;
							cloudId: string;
							children: React.ReactNode;
						}) => (
							<UserProfileCardForMention
								userId={userId}
								cloudId={mentionCloudId}
								orgId={orgId ?? ''}
								viewerId={viewerId ?? ''}
							>
								{children}
							</UserProfileCardForMention>
						),
					}
				: {}),
		});

		return ProviderFactory.create({
			mentionProvider,
			profilecardProvider,
		});
	}, [cloudId, orgId, productKey, viewerId]);

	// AkReactRenderer only exposes primary link clicks through eventHandlers.link.onClick.
	// Attach native listeners on the container to capture aux-click and context-menu
	// interactions that bypass the ADF link onClick path.
	useEffect(() => {
		if (!isBlueLinkAnalyticsEnabled) {
			return;
		}

		const container = rendererContainerRef.current;
		if (!container) {
			return;
		}

		const fireBlueLinkClick = ({
			url,
			isAuxClick,
			isContextMenu,
		}: {
			url: string;
			isAuxClick: boolean;
			isContextMenu: boolean;
		}) => {
			const {
				commonAttributes: latestCommonAttributes,
				fireAnalyticsEvent: latestFireAnalyticsEvent,
				fireSAINUsageEvent: latestFireSAINUsageEvent,
			} = blueLinkAnalyticsContextRef.current;

			latestFireAnalyticsEvent(
				onAIAnswerDialogClick({
					...latestCommonAttributes,
					elementInteracted: 'bluelink',
					isAuxClick,
					isContextMenu,
				}),
			);
			latestFireSAINUsageEvent({
				metCriteria: SainAction.BLUE_LINK_CLICKED,
				commonAttributes: latestCommonAttributes,
			});
		};

		const getContainedAnchorUrl = (event: MouseEvent) => {
			const target = event.target;
			if (!(target instanceof Element)) {
				return;
			}

			const anchor = target.closest('a[href]');
			if (!anchor || !container.contains(anchor)) {
				return;
			}

			const citationRoot = anchor.closest(SAIN_CITATION_SELECTOR);
			if (citationRoot && container.contains(citationRoot)) {
				return;
			}

			return anchor.getAttribute('href') || undefined;
		};

		const onAuxClick = (event: MouseEvent) => {
			if (event.button !== 1) {
				return;
			}

			const url = getContainedAnchorUrl(event);
			if (!url) {
				return;
			}

			fireBlueLinkClick({ url, isAuxClick: true, isContextMenu: false });
		};

		const onContextMenu = (event: MouseEvent) => {
			const url = getContainedAnchorUrl(event);
			if (!url) {
				return;
			}

			fireBlueLinkClick({ url, isAuxClick: false, isContextMenu: true });
		};

		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		container.addEventListener('auxclick', onAuxClick);
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		container.addEventListener('contextmenu', onContextMenu);
		return () => {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			container.removeEventListener('auxclick', onAuxClick);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			container.removeEventListener('contextmenu', onContextMenu);
		};
	}, [isBlueLinkAnalyticsEnabled]);

	const eventHandlers = useMemo(() => {
		if (!isBlueLinkAnalyticsEnabled) {
			return undefined;
		}

		return {
			link: {
				onClick: (event: React.SyntheticEvent<HTMLAnchorElement>, url: string | undefined) => {
					if (!url) {
						return;
					}

					const mouseEvent = event.nativeEvent as MouseEvent;
					// Middle-clicks are handled by the native auxclick listener above;
					// other non-primary buttons are intentionally ignored.
					if (mouseEvent.button !== 0) {
						return;
					}

					const isModifiedNavigation =
						mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey;

					const {
						commonAttributes: latestCommonAttributes,
						fireAnalyticsEvent: latestFireAnalyticsEvent,
						fireSAINUsageEvent: latestFireSAINUsageEvent,
					} = blueLinkAnalyticsContextRef.current;

					latestFireAnalyticsEvent(
						onAIAnswerDialogClick({
							...latestCommonAttributes,
							elementInteracted: 'bluelink',
							isAuxClick: false,
							isContextMenu: false,
						}),
					);
					latestFireSAINUsageEvent({
						metCriteria: SainAction.BLUE_LINK_CLICKED,
						commonAttributes: latestCommonAttributes,
					});

					// For unmodified left clicks, the page would normally navigate away before
					// the analytics request can be dispatched, so we intercept and open the link
					// manually in a new tab. Modifier-key clicks (cmd/ctrl/shift) are left to the
					// browser to honour the user's intent (background tab, new window, etc.) —
					// the analytics event still fires above.
					if (!isModifiedNavigation) {
						event.preventDefault();
						window.open(url, '_blank', 'noopener,noreferrer');
					}
				},
			},
		};
	}, [isBlueLinkAnalyticsEnabled]);

	return (
		<div ref={rendererContainerRef}>
			{isBlueLinkAnalyticsEnabled && hasBlueLinks && (
				<VisuallyHidden>
					<FormattedMessage {...messages.linksOpenInNewTab} />
				</VisuallyHidden>
			)}
			<Suspense fallback={<Fragment />}>
				<AkReactRenderer {...config} eventHandlers={eventHandlers} dataProviders={dataProviders} />
			</Suspense>
		</div>
	);
}
